<script lang="ts">
  /*
  Suggestions for further improvement:
  - Support for nested droppables and custom drop zones.
  - Add visual feedback (highlight) when an item is dragged over.
  - Allow custom validation for dropped items.
  - Expose drag enter/leave events for more control.
*/
  import { dragItem } from '../../store';
  import type { DropEvent } from '../utils/utils';
  import { initDropManager, registerDroppable } from '../utils/dropHandler';
  import { onMount, onDestroy } from 'svelte';

  interface Props {
    onDrop?: (item: DropEvent) => boolean | void;
    fillHeight?: boolean;
    itemArray: Array<any>;
    children?: import('svelte').Snippet;
  }

  let {
    onDrop = () => {
      return true; // default to true to accept all drops
    },
    fillHeight = true,
    itemArray,
    children,
  }: Props = $props();
  let element: HTMLElement;
  let registration: () => void;

  type DraggableItem = {
    data: any;
    element: HTMLElement;
    dropHandler: (event: MouseEvent, onDroppable: boolean, onSelf: boolean) => void;
  };

  const droppedOnCurrentContainer = (data: DropEvent) => {
    let item = data.item;
    if (!item) return;

    // Prevent dropping on this droppable if the item is already contained here
    if (itemArray && itemArray.indexOf(item.data) !== -1) {
      if (item.dropHandler) {
        item.dropHandler(data.position, true, true);
      }
      return;
    }

    // Pass both event and item as an object
    let result = onDrop({ position: data.position, item: item.data });
    if (result === false) {
      item.dropHandler(data.position, false, false);
      return; // if onDrop returns false, do not clear the drag item
    }
    dragItem.set(null);

    if (item.dropHandler) {
      item.dropHandler(data.position, true, false);
    }
  };

  onMount(() => {
    registration = registerDroppable(element, droppedOnCurrentContainer);
    initDropManager(); // sets up global mouseup once
  });

  onDestroy(() => {
    if (registration) registration();
  });
</script>

<div class="droppable {fillHeight ? 'fill-height' : ''}" role="region" bind:this={element}>
  {@render children?.()}
</div>

<style lang="less">
  .droppable {
    user-select: none;
    width: 100%;

    &.fill-height {
      height: 100%;
    }
  }
</style>
